import React, { useState } from 'react'

import { Box, InputBase, Stack, Typography, useTheme } from '@mui/material'
import { CircleAlert } from 'lucide-react'

import { space, font } from 'settings/tokens'

import { FieldLabel } from './FieldLabel'
import { FIELD_HEIGHT, fieldFocusSx, fieldSurfaceSx } from './fieldMetrics'

export interface CurrencyFieldProps {
  label?: React.ReactNode
  /** Major units (dollars), NOT cents. `undefined` = empty. */
  value?: number
  onChange: (value: number | undefined) => void
  /** ISO 4217. Only the symbol is rendered; the code travels with the request. */
  currency?: string
  error?: string
  /** Fires when the field loses focus. Callers use it to decide when an error may show. */
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
}

/**
 * VERA-632 — the app's money-entry field.
 *
 * Built here rather than in `@repo/ui` deliberately: it composes `FieldLabel`,
 * `fieldSurfaceSx` and `FIELD_HEIGHT`, all of which are app-local and tuned for
 * `apps/app`'s 8px spacing base. A `@repo/ui` version would inherit the 4px theme
 * and render at the wrong size here — the same mismatch that forced `FieldLabel`
 * to be re-implemented rather than imported.
 *
 * ## Three things it fixes, all present in `TransferOwnership.tsx`
 *
 * 1. **The symbol is ALWAYS visible.** That file renders `$` only once the field is
 *    non-empty, so the currency marker appears and disappears as you type — the one
 *    moment a user is deciding whether "1500" means dollars is exactly when it is
 *    absent. An empty money field with no symbol is just a number box.
 *
 * 2. **`inputMode="decimal"`, not `type="number"`.** `type="number"` brings spinner
 *    arrows nobody wants on a price, silently ignores `maxLength` (that file sets one
 *    anyway), and lets `e`, `+` and `-` through because they are valid in exponent
 *    notation. `inputMode` gets the numeric keypad on a phone without any of it.
 *
 * 3. **Two decimal places, enforced while typing.** `transfers.price` is
 *    `numeric(14,2)` and the API rejects a third decimal, so catching it at keystroke
 *    beats a server error after the user has moved on.
 *
 * ## Value shape
 *
 * Emits **major units** (dollars) to match the API's `price` field. The escrow row
 * stores minor units, and that conversion happens server-side — a form that emitted
 * cents would silently disagree with every other price field in the app.
 */
export default function CurrencyField({
  label,
  value,
  onChange,
  currency = 'USD',
  error,
  onBlur,
  placeholder,
  disabled,
  autoFocus,
}: CurrencyFieldProps) {
  const theme = useTheme()

  // Held as text while focused so a half-typed "12." survives a re-render. Parsing on
  // every keystroke would rewrite "12." to "12" and eat the decimal point the user is
  // in the middle of typing.
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft ?? (value === undefined ? '' : String(value))

  const handleChange = (raw: string) => {
    // Strip everything that is not a digit or a single dot, then clamp to 2dp.
    const cleaned = raw.replace(/[^\d.]/g, '')
    const parts = cleaned.split('.')
    const normalised =
      parts.length > 1
        ? `${parts[0]}.${parts.slice(1).join('').slice(0, 2)}`
        : parts[0]

    setDraft(normalised)
    if (normalised === '' || normalised === '.') {
      onChange(undefined)
      return
    }
    const parsed = Number(normalised)
    onChange(Number.isFinite(parsed) ? parsed : undefined)
  }

  const symbol = currency === 'USD' ? '$' : currency

  return (
    <Box>
      {label ? <FieldLabel label={label} /> : null}

      <Stack
        direction='row'
        alignItems='center'
        sx={{
          ...fieldSurfaceSx(theme),
          ...fieldFocusSx,
          height: FIELD_HEIGHT,
          paddingX: `${space.md}px`,
          gap: `${space.xs}px`,
          opacity: disabled ? 0.5 : 1,
          // Error border must beat the `!important` in fieldSurfaceSx, which exists
          // because index.css's `#vera-shadow-root *` rule wins on ID specificity.
          ...(error
            ? {
                border: `1px solid ${theme.palette.error.main} !important`,
                '&:hover, &.Mui-focused, &:focus-within': {
                  border: `1px solid ${theme.palette.error.main} !important`,
                  boxShadow: 'none',
                },
              }
            : {}),
        }}
      >
        <Typography
          component='span'
          sx={{
            fontSize: `${font.body}px`,
            // Muted until there is a value, so the symbol reads as part of the
            // placeholder rather than as content the user typed.
            color: shown ? 'text.primary' : 'text.disabled',
            flexShrink: 0,
          }}
        >
          {symbol}
        </Typography>

        <InputBase
          value={shown}
          onChange={e => handleChange(e.target.value)}
          onBlur={() => {
            setDraft(null)
            onBlur?.()
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          inputProps={{
            inputMode: 'decimal',
            // Belt and braces alongside handleChange: a paste bypasses keystroke
            // filtering, and this keeps the OS keypad numeric.
            pattern: '[0-9]*\\.?[0-9]*',
            'aria-label': typeof label === 'string' ? label : 'Price',
            'aria-invalid': Boolean(error),
          }}
          sx={{
            flex: 1,
            fontSize: `${font.body}px`,
            '& input': { padding: 0 },
            /*
              Strip the InputBase's OWN chrome. `settings/mui/MuiInputBase.ts` gives every
              InputBase a border and radius so a bare one is field-shaped by default — correct
              for a standalone input, wrong here, where this sits INSIDE a Stack that already
              carries `fieldSurfaceSx`. Measured in the browser: a `1px solid rgb(228,228,231)`
              rectangle nested inside the field's own border, i.e. a visible double outline.
              The outer Stack owns the surface; the inner input is just the text.
            */
            border: 'none !important',
            borderRadius: 0,
            background: 'transparent',
            padding: 0,
            minHeight: 0,
            '&:hover, &.Mui-focused, &:focus-within': {
              border: 'none !important',
              boxShadow: 'none',
            },
          }}
        />
      </Stack>

      {error ? (
        <Stack
          direction='row'
          alignItems='flex-start'
          sx={{ gap: `${space.sm}px`, marginTop: `${space.sm}px` }}
        >
          <CircleAlert
            size={16}
            strokeWidth={1.5}
            color={theme.palette.error.main}
            style={{ flexShrink: 0, marginTop: 2 }}
          />
          <Typography
            sx={{
              fontSize: `${font.label}px`,
              color: 'error.main',
              // 1.5 rather than the default: the designed copy runs to two lines at
              // 375px, and dense error text is the least readable text on a form.
              lineHeight: 1.5,
            }}
          >
            {error}
          </Typography>
        </Stack>
      ) : null}
    </Box>
  )
}
